// Generated by GitHub Copilot
import { createRoot } from "react-dom/client";
import "./index.css";
import packageJson from "../package.json";

import AppWrapper from "./AppWrapper";

import { BrowserRouter } from "react-router-dom";
import { JSErrors } from "@newrelic/browser-agent/features/jserrors";
import { PageViewEvent } from "@newrelic/browser-agent/features/page_view_event";
import { PageViewTiming } from "@newrelic/browser-agent/features/page_view_timing";
import { SoftNav } from "@newrelic/browser-agent/features/soft_navigations";
import { GenericEvents } from "@newrelic/browser-agent/features/generic_events";
import { SessionTrace } from "@newrelic/browser-agent/features/session_trace";
import { SessionReplay } from "@newrelic/browser-agent/features/session_replay";
import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";

// NewRelic configuration - only initialize if credentials are provided
let browserAgent = null;

const newrelic_license_key = process.env.REACT_APP_NEWRELIC_LICENSE_KEY || "";
const newrelic_application_id = process.env.REACT_APP_NEWRELIC_APPLICATION_ID || "";
const newrelic_account_id = process.env.REACT_APP_NEWRELIC_ACCOUNT_ID || "";

// Only initialize NewRelic if all required credentials are provided
if (newrelic_license_key && newrelic_application_id && newrelic_account_id) {
  const options = {
    features: [
      JSErrors,
      PageViewEvent,
      PageViewTiming,
      SoftNav,
      GenericEvents,
      SessionTrace,
      SessionReplay,
    ],
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: ["bam.nr-data.net"], capture_payloads: false },
      session_trace: { enabled: true, harvestTimeSeconds: 10 },
      session_replay: {
        enabled: true,
        block_selector: "[data-nr-block], .incident-notes, .problem-notes",
        mask_text_selector: "[data-nr-mask]",
        sampling_rate: 10.0,
        error_sampling_rate: 100.0,
        mask_all_inputs: false,
        collect_fonts: true,
        inline_images: false,
        inline_stylesheet: true,
        fix_stylesheets: true,
        preload: true,
        mask_input_options: {},
      },
      session: {
        expiresMs: 14400000,
        inactiveMs: 1800000,
      },
      performance: { capture_measures: true },
      browser_consent_mode: { enabled: false },
    },
    loader_config: {
      accountID: newrelic_account_id,
      trustKey: "1190893",
      agentID: newrelic_application_id, // NR loader_config requires agentID = applicationID (same value, different field name)
      licenseKey: newrelic_license_key,
      applicationID: newrelic_application_id,
    },
    info: {
      beacon: "bam.nr-data.net",
      errorBeacon: "bam.nr-data.net",
      licenseKey: newrelic_license_key,
      applicationID: newrelic_application_id,
      sa: 1,
    },
  };

  browserAgent = new BrowserAgent(options);
  if (process.env.NODE_ENV === "development") {
    console.log("NewRelic browser monitoring enabled");
  }
} else {
  // Create a no-op agent that implements the same interface
  browserAgent = {
    setPageViewName: () => {},
    recordCustomEvent: () => {},
    addPageAction: () => {},
    noticeError: () => {},
    setUserId: () => {},
    setCustomAttribute: () => {},
    interaction: () => ({
      setName: () => {},
      setAttribute: () => {},
      end: () => {},
    }),
  };
  if (process.env.NODE_ENV === "development") {
    console.log(
      "NewRelic browser monitoring disabled (no credentials provided)",
    );
  }
}

// Wire errorTracker abstraction to NewRelic browser agent.
// Hooks and services use errorLogging.js (logError/logWarning) which calls
// window.errorTracker — without this, those errors are silently dropped.
// writable:false prevents assignment shadowing by third-party scripts.
// configurable:true allows provider replacement and test overrides via Object.defineProperty.
Object.defineProperty(window, "errorTracker", {
  value: {
    captureException: (err, ctx) => browserAgent?.noticeError(err, ctx),
    captureMessage: (msg, level, ctx) =>
      browserAgent?.recordCustomEvent("AppWarning", { message: msg, level, ...ctx }),
  },
  writable: false,
  configurable: true,
});

global.appVersion = packageJson.version;

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <BrowserRouter>
    <AppWrapper NewRelic={browserAgent} />
  </BrowserRouter>,
);
